import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { FaIcon } from 'client/js/util/layout_utils';
import Callout from 'client/js/util/callout';
import { useFileList, useMoveItem, useCopyItem, useDeleteItem, useRenameItem, useZipItem, useUnzipItem } from './hooks/use_filemanager';
import FileList from './file_list';
import Toolbar from './toolbar';
import UploadModal from './upload_modal';
import CreateItemModal from './create_item_modal';
import RenameModal from './rename_modal';
import ChmodModal from './chmod_modal';
import ContextMenu from './context_menu';
import DiskUsageModal from './disk_usage_modal';

const FileBrowser = ({ paneId, className = '', sharedClipboard, setSharedClipboard, baseApiUrl, onEditFile, onViewImage, onPathChange, isVisible = true }) => {
  const [currentPath, setCurrentPath] = useState('/');
  const [selectedItemPaths, setSelectedItemPaths] = useState(() => new Set());

  // Notify parent of initial path on mount
  useEffect(() => {
    if (onPathChange) {
      onPathChange(currentPath);
    }
  }, []); // Only run on mount
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showChmodModal, setShowChmodModal] = useState(false);
  const [showDiskUsageModal, setShowDiskUsageModal] = useState(false);
  const [diskUsagePaths, setDiskUsagePaths] = useState(null);
  const [renamingItem, setRenamingItem] = useState(null);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragOperation, setDragOperation] = useState(null); // 'move' or 'copy'
  const [dragItemCount, setDragItemCount] = useState(0);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(-1);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadPath, setUploadPath] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const browserRef = useRef(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useFileList(currentPath, baseApiUrl);
  const moveItemMutation = useMoveItem(baseApiUrl);
  const copyItemMutation = useCopyItem(baseApiUrl);
  const deleteItemMutation = useDeleteItem(baseApiUrl);
  const renameItemMutation = useRenameItem(baseApiUrl);
  const zipItemMutation = useZipItem(baseApiUrl);
  const unzipItemMutation = useUnzipItem(baseApiUrl);

  const sortedFiles = useMemo(() => {
    if (!data?.files) return [];
    const files = [...data.files];

    return files.sort((a, b) => {
      // Always keep directories on top
      if (a.type !== b.type) {
        return a.type === 'directory' ? -1 : 1;
      }

      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Case insensitive sort for names
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  const filesByPath = useMemo(() => {
    return new Map(sortedFiles.map(file => [file.path, file]));
  }, [sortedFiles]);

  const selectedItems = useMemo(() => {
    return Array.from(selectedItemPaths).map(path => filesByPath.get(path)).filter(Boolean);
  }, [selectedItemPaths, filesByPath]);

  // --- Navigation & Basic Actions ---

  const isImageFile = useCallback((filename) => {
    const imageExtensions = [
      'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico', 'tiff', 'tif'
    ];
    const ext = filename.split('.').pop().toLowerCase();
    return imageExtensions.includes(ext);
  }, []);

  const isEditableFile = useCallback((filename) => {
    const editableExtensions = [
      // Text & Config
      'txt', 'text', 'log', 'conf', 'config', 'ini', 'cfg',
      // Web
      'html', 'htm', 'css', 'scss', 'sass', 'less',
      'js', 'jsx', 'ts', 'tsx', 'json', 'xml',
      // Programming
      'php', 'php3', 'php4', 'php5', 'phtml',
      'py', 'rb', 'java', 'c', 'cpp', 'h', 'hpp',
      'go', 'rs', 'swift', 'kt',
      // Shell & Scripts
      'sh', 'bash', 'zsh', 'fish',
      // Markup & Data
      'md', 'markdown', 'yml', 'yaml', 'toml',
      'sql', 'csv', 'tsv',
      // Templates
      'erb', 'ejs', 'hbs', 'handlebars', 'twig', 'blade',
      // Other
      'env', 'gitignore', 'htaccess', 'dockerfile'
    ];

    const ext = filename.split('.').pop().toLowerCase();
    return editableExtensions.includes(ext) || !filename.includes('.');
  }, []);

  const handleNavigate = useCallback((path) => {
    setCurrentPath(path);
    setSelectedItemPaths(new Set());
    setLastSelectedIndex(-1);
    if (onPathChange) {
      onPathChange(path);
    }
  }, [onPathChange]);

  const handleNavigateUp = useCallback(() => {
    if (currentPath === '/') return;
    const parentPath = currentPath.split('/').slice(0, -1).join('/') || '/';
    handleNavigate(parentPath);
  }, [currentPath, handleNavigate]);

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['filemanager', 'list', currentPath, baseApiUrl] });
  }, [currentPath, baseApiUrl, queryClient]);

  const handleEdit = useCallback((item) => {
    if (!isEditableFile(item.name)) {
      toast.warning(`Die Datei "${item.name}" kann nicht im Editor geöffnet werden. Nur Text- und Code-Dateien sind editierbar.`);
      return;
    }
    onEditFile(item);
  }, [isEditableFile, onEditFile]);

  const handleViewImage = useCallback((item) => {
    onViewImage(item);
  }, [onViewImage]);

  const handleDoubleClick = useCallback((item) => {
    if (item.type === 'directory') {
      handleNavigate(item.path);
    } else if (isImageFile(item.name)) {
      handleViewImage(item);
    } else {
      handleEdit(item);
    }
  }, [handleNavigate, handleEdit, handleViewImage, isImageFile]);

  // --- Helpers ---

  const createProgressToast = useCallback((current, total, operation) => {
    const progressPercentage = total > 0 ? Math.round((current / total) * 100) : 0;
    const operationText = operation === 'copy' ? 'Kopiere' : operation === 'move' ? 'Verschiebe' : 'Lösche';

    return (
      <div>
        <div className="mb-2">
          <strong>{operationText}...</strong>
        </div>
        <div className="progress mb-2" style={{ height: '8px' }}>
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        <small>{current} / {total} Elemente</small>
      </div>
    );
  }, []);

  // --- File Operations ---

  const handleRenameSubmit = useCallback((item, newName) => {
    if (newName && newName !== item.name) {
      renameItemMutation.mutate({
        path: item.path,
        newName,
        currentPath
      }, {
        onSuccess: () => {
          setRenamingItem(null);
          if (browserRef.current) {
            browserRef.current.focus();
          }
        }
      });
    } else {
        // If no rename actually happens (e.g., name is same), still refocus
        if (browserRef.current) {
            browserRef.current.focus();
        }
        setRenamingItem(null);
    }
  }, [renameItemMutation, currentPath, browserRef]);

  const handleRenameCancel = useCallback(() => {
    setRenamingItem(null);
    // Refocus the browser to restore keyboard navigation
    if (browserRef.current) {
      browserRef.current.focus();
    }
  }, []);

  const handleDelete = useCallback(async () => {
    if (selectedItems.length === 0) return;

    if (!confirm(`Möchten Sie ${selectedItems.length} Element(e) wirklich löschen?`)) {
      return;
    }

    // Show progress toast for multiple items
    if (selectedItems.length > 1) {
      const toastId = toast.info(createProgressToast(0, selectedItems.length, 'delete'), {
        autoClose: false,
        closeButton: false,
        closeOnClick: false,
        draggable: false,
      });

      let completed = 0;

      try {
        for (let i = 0; i < selectedItems.length; i++) {
          const item = selectedItems[i];
          const skipRefresh = i < selectedItems.length - 1; // Only refresh on last item

          await deleteItemMutation.mutateAsync({
            path: item.path,
            currentPath: currentPath,
            skipRefresh,
            skipToast: true
          });

          completed++;
          toast.update(toastId, {
            render: createProgressToast(completed, selectedItems.length, 'delete')
          });
        }

        toast.update(toastId, {
          render: `${completed} Elemente gelöscht`,
          type: 'success',
          autoClose: 3000,
          closeButton: true,
          draggable: true
        });
        setSelectedItemPaths(new Set());
      } catch {
        toast.update(toastId, {
          render: 'Fehler beim Löschen der Elemente',
          type: 'error',
          autoClose: 5000
        });
      }
    } else {
      // Single item - use normal mutation without progress toast
      deleteItemMutation.mutate({
        path: selectedItems[0].path,
        currentPath: currentPath,
      }, {
        onSuccess: () => setSelectedItemPaths(new Set())
      });
    }
  }, [selectedItems, currentPath, deleteItemMutation, createProgressToast]);

  const handlePaste = useCallback(async () => {
    if (!sharedClipboard || sharedClipboard.items.length === 0) return;

    const items = sharedClipboard.items;
    const operation = sharedClipboard.operation;

    // Filter out items that are being pasted into their own parent directory (no-op)
    const validItems = items.filter(item => {
      const parentPath = item.path.substring(0, item.path.lastIndexOf('/')) || '/';
      // For move: always no-op
      // For copy: also no-op based on user request, or potentially duplicating?
      // Requirement says "should be a no-op".
      return parentPath !== currentPath;
    });

    if (validItems.length === 0) {
      // Nothing to do
      if (operation === 'cut') {
         setSharedClipboard(null);
      }
      return;
    }

    // Show progress toast for multiple items
    if (validItems.length > 1) {
      const toastId = toast.info(createProgressToast(0, validItems.length, operation), {
        autoClose: false,
        closeButton: false,
        closeOnClick: false,
        draggable: false,
      });

      let completed = 0;

      try {
        for (let i = 0; i < validItems.length; i++) {
          const item = validItems[i];
          const skipRefresh = i < validItems.length - 1; // Only refresh on last item

          if (operation === 'copy') {
            await copyItemMutation.mutateAsync({
              sourcePath: item.path,
              destinationPath: currentPath,
              skipRefresh,
              skipToast: true
            });
          } else if (operation === 'cut') {
            await moveItemMutation.mutateAsync({
              sourcePath: item.path,
              destinationPath: currentPath,
              currentPath: currentPath,
              skipRefresh,
              skipToast: true
            });
          }

          completed++;
          toast.update(toastId, {
            render: createProgressToast(completed, validItems.length, operation)
          });
        }

        const operationText = operation === 'copy' ? 'kopiert' : 'verschoben';
        toast.update(toastId, {
          render: `${completed} Elemente ${operationText}`,
          type: 'success',
          autoClose: 3000,
          closeButton: true,
          draggable: true
        });
      } catch {
        toast.update(toastId, {
          render: 'Fehler beim Verarbeiten der Elemente',
          type: 'error',
          autoClose: 5000
        });
      }
    } else {
      // Single item - use normal mutation without progress toast
      const item = validItems[0];
      if (operation === 'copy') {
        copyItemMutation.mutate({
          sourcePath: item.path,
          destinationPath: currentPath,
        });
      } else if (operation === 'cut') {
        moveItemMutation.mutate({
          sourcePath: item.path,
          destinationPath: currentPath,
          currentPath: currentPath,
        });
      }
    }

    if (operation === 'cut') {
      setSharedClipboard(null);
    }
  }, [sharedClipboard, currentPath, copyItemMutation, moveItemMutation, createProgressToast, setSharedClipboard]);

  const handleZip = useCallback(() => {
    if (selectedItems.length === 0) return;

    // Determine default archive name
    let defaultName;
    if (selectedItems.length === 1) {
      // Single item: use its name without extension
      const itemName = selectedItems[0].name;
      const lastDotIndex = itemName.lastIndexOf('.');
      defaultName = lastDotIndex > 0 ? itemName.substring(0, lastDotIndex) : itemName;
    } else {
      // Multiple items: use current directory name or 'archiv' for root
      if (currentPath === '/') {
        defaultName = 'archiv';
      } else {
        // Get last part of path
        const pathParts = currentPath.split('/').filter(Boolean);
        defaultName = pathParts[pathParts.length - 1] || 'archiv';
      }
    }

    const archiveName = prompt('Name des Archivs (ohne .zip):', defaultName);
    if (!archiveName) return;

    const toastId = toast.info('Erstelle Archiv...', {
      autoClose: false,
      closeButton: false,
      closeOnClick: false,
      draggable: false,
    });

    zipItemMutation.mutate({
      paths: selectedItems.map(item => item.path),
      archiveName: `${archiveName}.zip`,
      destinationPath: currentPath,
      skipToast: true
    }, {
      onSuccess: () => {
        setSelectedItemPaths(new Set());
        toast.update(toastId, {
          render: 'Archiv erfolgreich erstellt',
          type: 'success',
          autoClose: 3000,
          closeButton: true,
          draggable: true
        });
      },
      onError: (error) => {
        toast.update(toastId, {
          render: error.message || 'Fehler beim Erstellen des Archivs',
          type: 'error',
          autoClose: 5000,
          closeButton: true,
          draggable: true
        });
      }
    });
  }, [selectedItems, currentPath, zipItemMutation]);

  const handleUnzip = useCallback((item) => {
    const toastId = toast.info('Entpacke Archiv...', {
      autoClose: false,
      closeButton: false,
      closeOnClick: false,
      draggable: false,
    });

    unzipItemMutation.mutate({
      archivePath: item.path,
      destinationPath: currentPath,
      skipToast: true
    }, {
      onSuccess: () => {
        toast.update(toastId, {
          render: 'Archiv erfolgreich entpackt',
          type: 'success',
          autoClose: 3000,
          closeButton: true,
          draggable: true
        });
      },
      onError: (error) => {
        toast.update(toastId, {
          render: error.message || 'Fehler beim Entpacken des Archivs',
          type: 'error',
          autoClose: 5000,
          closeButton: true,
          draggable: true
        });
      }
    });
  }, [currentPath, unzipItemMutation]);

  const handleDiskUsage = useCallback(() => {
    // If items are selected, use the first one (if it's a directory) or all selected paths
    // Otherwise, use the current path
    if (selectedItems.length > 0) {
      const dirItems = selectedItems.filter(item => item.type === 'directory');
      if (dirItems.length > 0) {
        setDiskUsagePaths(dirItems.map(item => item.path));
      } else {
        setDiskUsagePaths(selectedItems.map(item => item.path));
      }
    } else {
      setDiskUsagePaths(null); // Will use currentPath in modal
    }
    setShowDiskUsageModal(true);
  }, [selectedItems]);

  const handleCopy = useCallback(() => {
    if (selectedItems.length === 0) return;
    setSharedClipboard({ items: selectedItems, operation: 'copy' });
  }, [selectedItems, setSharedClipboard]);

  const handleCut = useCallback(() => {
    if (selectedItems.length === 0) return;
    setSharedClipboard({ items: selectedItems, operation: 'cut' });
  }, [selectedItems, setSharedClipboard]);

  const handleRename = useCallback(() => {
    if (selectedItems.length !== 1) return;
    setRenamingItem(selectedItems[0]);
  }, [selectedItems]);

  const handleChmod = useCallback(() => {
    if (selectedItems.length !== 1) return;
    setItemToEdit(selectedItems[0]);
    setShowChmodModal(true);
  }, [selectedItems]);

  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const handleSelectItem = useCallback((item, isSelected, index = null) => {
    // Clear clipboard when selecting different items
    if (sharedClipboard && sharedClipboard.items.length > 0) {
      const clipboardPaths = sharedClipboard.items.map(i => i.path);
      if (!clipboardPaths.includes(item.path)) {
        setSharedClipboard(null);
      }
    }

    if (isSelected) {
      setSelectedItemPaths(prev => {
        const next = new Set(prev);
        next.add(item.path);
        return next;
      });
    } else {
      setSelectedItemPaths(prev => {
        const next = new Set(prev);
        next.delete(item.path);
        return next;
      });
    }

    // Update lastSelectedIndex if provided (for range selection)
    if (index !== null) {
      setLastSelectedIndex(index);
    }
  }, [sharedClipboard, setSharedClipboard]);

  // --- More Handlers depending on sortedFiles ---

  const handleItemClick = useCallback((item, index, e) => {
    // Use sortedFiles for correct range selection
    const files = sortedFiles;

    if (e.ctrlKey || e.metaKey) {
      // Toggle selection
      const isSelected = selectedItemPaths.has(item.path);
      handleSelectItem(item, !isSelected);
      setLastSelectedIndex(index);
    } else if (e.shiftKey) {
      // Range selection
      e.preventDefault(); // Prevent text selection
      if (lastSelectedIndex === -1) {
        setSelectedItemPaths(new Set([item.path]));
        setLastSelectedIndex(index);
      } else {
        const start = Math.min(lastSelectedIndex, index);
        const end = Math.max(lastSelectedIndex, index);
        const rangeItems = files.slice(start, end + 1);
        setSelectedItemPaths(new Set(rangeItems.map(rangeItem => rangeItem.path)));
      }
    } else {
      // Single selection
      setSelectedItemPaths(new Set([item.path]));
      setLastSelectedIndex(index);
    }
  }, [selectedItemPaths, lastSelectedIndex, sortedFiles, handleSelectItem]);

  // --- Drag & Drop ---

  const handleExternalFileDrop = useCallback((files, targetPath) => {
    setUploadFiles(files);
    setUploadPath(targetPath);
    setShowUploadModal(true);
  }, []);

  const handleBrowserClick = useCallback((e) => {
    if (e.target.closest('button') || e.target.closest('input') || e.target.closest('a')) {
      return;
    }
    if (browserRef.current) {
      browserRef.current.focus();
    }
  }, []);

  const handlePaneDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const dragDataString = e.dataTransfer.getData('application/json');
      if (dragDataString) {
        const dragData = JSON.parse(dragDataString);
        setDragItemCount(dragData.items?.length || 1);
      }
    } catch {
      // Ignore
    }
    const operation = e.shiftKey ? 'copy' : 'move';
    setDragOperation(operation);
    e.dataTransfer.dropEffect = operation;
    setIsDragOver(true);
  }, []);

  const handlePaneDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setDragOperation(null);
  }, []);

  const processDroppedItems = useCallback(async (items, targetPath, operation) => {
    if (!items || items.length === 0) return;
    const validItems = items.filter(item => {
      const parentPath = item.path.substring(0, item.path.lastIndexOf('/')) || '/';
      return parentPath !== targetPath;
    });

    if (validItems.length === 0) return;

    const toastId = toast.info(createProgressToast(0, validItems.length, operation), {
      autoClose: false,
      closeButton: false,
      closeOnClick: false,
      draggable: false,
    });

    let completed = 0;

    try {
      for (let i = 0; i < validItems.length; i++) {
        const item = validItems[i];
        const skipRefresh = i < validItems.length - 1;
        const skipToast = true;

        if (operation === 'copy') {
          await copyItemMutation.mutateAsync({
            sourcePath: item.path,
            destinationPath: targetPath,
            skipRefresh,
            skipToast
          });
        } else {
          await moveItemMutation.mutateAsync({
            sourcePath: item.path,
            destinationPath: targetPath,
            currentPath: item.path.substring(0, item.path.lastIndexOf('/')) || '/',
            skipRefresh,
            skipToast
          });
        }

        completed++;
        toast.update(toastId, {
          render: createProgressToast(completed, validItems.length, operation)
        });
      }

      const operationText = operation === 'copy' ? 'kopiert' : 'verschoben';
      toast.update(toastId, {
        render: `${completed} Elemente ${operationText}`,
        type: 'success',
        autoClose: 3000,
        closeButton: true,
        draggable: true
      });
    } catch (error) {
      console.error('Drop operation error:', error);
      toast.update(toastId, {
        render: 'Fehler bei der Operation',
        type: 'error',
        autoClose: 5000
      });
    }
  }, [copyItemMutation, moveItemMutation, createProgressToast]);

  const handlePaneDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      handleExternalFileDrop(files, currentPath);
      return;
    }

    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      const { items, sourcePaneId } = dragData;

      if (sourcePaneId === paneId && currentPath === dragData.sourcePath) {
        setDragOperation(null);
        return;
      }

      const operation = e.shiftKey ? 'copy' : 'move';
      processDroppedItems(items, currentPath, operation);
    } catch (err) {
      console.error('Error handling pane drop:', err);
    }

    setDragOperation(null);
  }, [paneId, currentPath, processDroppedItems, handleExternalFileDrop]);

  const handleItemDrop = useCallback((e, targetItem) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setDragOperation(null);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      handleExternalFileDrop(files, targetItem.path);
      return;
    }

    try {
      const dragDataString = e.dataTransfer.getData('application/json');
      if (!dragDataString) return;

      const dragData = JSON.parse(dragDataString);
      const { items } = dragData;

      if (items.some(i => i.path === targetItem.path)) {
        return;
      }

      const operation = e.shiftKey ? 'copy' : 'move';
      processDroppedItems(items, targetItem.path, operation);

    } catch (err) {
      console.error('Error handling item drop:', err);
    }
  }, [processDroppedItems, handleExternalFileDrop]);

  const handleKeyDown = useCallback((e) => {
    if (isLoading || error) return;
    if (showUploadModal || showCreateModal || showRenameModal || showChmodModal || showDiskUsageModal) return;

    if (e.key === 'Backspace') {
      e.preventDefault();
      handleNavigateUp();
      return;
    }

    if (e.key === 'Delete') {
      e.preventDefault();
      if (sortedFiles.length > 0) handleDelete();
    } else if (e.key === 'F2') {
      e.preventDefault();
      if (sortedFiles.length > 0) handleRename();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedItems.length === 1) {
        handleDoubleClick(selectedItems[0]);
      }
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();

      if (!sortedFiles.length) return;

      const direction = e.key === 'ArrowDown' ? 1 : -1;
      let nextIndex;

      if (lastSelectedIndex === -1) {
        nextIndex = direction === 1 ? 0 : sortedFiles.length - 1;
      } else {
        nextIndex = lastSelectedIndex + direction;
        if (nextIndex < 0) nextIndex = 0;
        if (nextIndex >= sortedFiles.length) nextIndex = sortedFiles.length - 1;
      }

      const item = sortedFiles[nextIndex];
      if (item) {
        setSelectedItemPaths(new Set([item.path]));
        setLastSelectedIndex(nextIndex);
      }
    } else if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (sortedFiles.length > 0) {
        setSelectedItemPaths(new Set(sortedFiles.map(item => item.path)));
      }
    }
  }, [isLoading, error, sortedFiles, showUploadModal, showCreateModal, showRenameModal, showChmodModal, showDiskUsageModal, handleDelete, handleRename, handleDoubleClick, selectedItems, lastSelectedIndex, handleNavigateUp]);

  // --- Context Menu ---

  const handleContextMenu = useCallback((e, item) => {
    e.preventDefault();

    if (!item) {
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        item: null
      });
      return;
    }

    if (!selectedItemPaths.has(item.path)) {
      setSelectedItemPaths(new Set([item.path]));
      setLastSelectedIndex(sortedFiles.indexOf(item));
    }

    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      item: item
    });
  }, [selectedItemPaths, sortedFiles]);

  const getContextMenuItems = () => {
    if (!contextMenu) return [];

    const item = contextMenu.item;

    if (!item) {
      const pasteLabel = sharedClipboard && sharedClipboard.items.length > 0
        ? `Einfügen (${sharedClipboard.items.length} Element${sharedClipboard.items.length !== 1 ? 'e' : ''})`
        : 'Einfügen';

      const items = [
        {
          label: pasteLabel,
          icon: 'fa-paste',
          action: handlePaste,
          disabled: !sharedClipboard || sharedClipboard.items.length === 0
        },
        'separator',
        {
          label: 'Neuer Ordner',
          icon: 'fa-folder-o',
          action: () => setShowCreateModal(true)
        },
        {
          label: 'Upload',
          icon: 'fa-upload',
          action: () => setShowUploadModal(true)
        },
        'separator',
        {
          label: 'Aktualisieren',
          icon: 'fa-refresh',
          action: () => handleNavigate(currentPath)
        }
      ];
      return items;
    }

    const isZip = item.name.toLowerCase().endsWith('.zip');
    const isEditable = item.type === 'file' && isEditableFile(item.name);
    const isImage = item.type === 'file' && isImageFile(item.name);

    const items = [
      {
        label: 'Öffnen',
        icon: 'fa-folder-open',
        action: () => handleDoubleClick(item),
        disabled: item.type !== 'directory'
      },
      isImage ? {
        label: 'Anzeigen',
        icon: 'fa-image',
        action: () => handleViewImage(item)
      } : null,
      isEditable ? {
        label: 'Bearbeiten',
        icon: 'fa-pencil',
        action: () => handleEdit(item)
      } : null,
      'separator',
      {
        label: 'Umbenennen',
        icon: 'fa-i-cursor',
        action: () => {
          setRenamingItem(item);
        }
      },
      {
        label: 'Kopieren',
        icon: 'fa-copy',
        action: handleCopy
      },
      {
        label: 'Ausschneiden',
        icon: 'fa-cut',
        action: handleCut
      },
      'separator',
      {
        label: 'Archiv erstellen',
        icon: 'fa-file-archive-o',
        action: handleZip
      },
      isZip ? {
        label: 'Entpacken',
        icon: 'fa-file-archive-o',
        action: () => handleUnzip(item)
      } : null,
      'separator',
      {
        label: 'Berechtigungen',
        icon: 'fa-lock',
        action: () => {
          setItemToEdit(item);
          setShowChmodModal(true);
        }
      },
      {
        label: 'Speicherplatzbelegung',
        icon: 'fa-pie-chart',
        action: () => {
          setSelectedItemPaths(new Set([item.path]));
          setDiskUsagePaths([item.path]);
          setShowDiskUsageModal(true);
        },
        disabled: item.type !== 'directory'
      },
      {
        label: 'Herunterladen',
        icon: 'fa-download',
        action: () => {
           const baseUrl = baseApiUrl || '/usercp/filemanager/api';
           window.location.href = `${baseUrl}/download?path=${encodeURIComponent(item.path)}`;
        },
        disabled: item.type === 'directory'
      },
      'separator',
      {
        label: 'Löschen',
        icon: 'fa-trash',
        action: handleDelete
      }
    ].filter(Boolean);

    return items;
  };

  const getBreadcrumbs = () => {
    if (currentPath === '/') return [{ name: <FaIcon name="home" />, path: '/' }];

    const parts = currentPath.split('/').filter(Boolean);
    const breadcrumbs = [{ name: <FaIcon name="home" />, path: '/' }];

    parts.forEach((part, index) => {
      const path = '/' + parts.slice(0, index + 1).join('/');
      breadcrumbs.push({ name: part, path });
    });

    return breadcrumbs;
  };

  if (error) {
    return (
      <div className={`file-browser ${className}`}>
        <Callout calloutClass="danger" title="Fehler">
          Fehler beim Laden der Dateien
        </Callout>
      </div>
    );
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex
    <div
      role="region"
      aria-label="Dateimanager"
      ref={browserRef}
      className={`file-browser ${className} ${isDragOver ? 'drag-over-pane' : ''}`}
      data-pane-id={paneId}
      onDragOver={handlePaneDragOver}
      onDragLeave={handlePaneDragLeave}
      onDrop={handlePaneDrop}
      onKeyDown={handleKeyDown}
      onClick={handleBrowserClick}
      tabIndex="0" // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
      style={{
        position: 'relative',
        outline: 'none',
        ...(isDragOver && {
          backgroundColor: dragOperation === 'copy' ? '#e7f3ff' : '#fff3cd',
          border: `2px dashed ${dragOperation === 'copy' ? '#007bff' : '#ffc107'}`,
        })
      }}
    >
      {isDragOver && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            textAlign: 'center',
            pointerEvents: 'none'
          }}
        >
          <i className={`fa ${dragOperation === 'copy' ? 'fa-copy' : 'fa-arrows'} fa-3x mb-2`} style={{ color: dragOperation === 'copy' ? '#007bff' : '#ffc107' }} />
          <div>
            <strong>{dragOperation === 'copy' ? 'Kopieren' : 'Verschieben'}</strong>
            {dragItemCount > 1 && (
              <div>
                <span className="badge badge-primary">{dragItemCount} Elemente</span>
              </div>
            )}
            <br />
            <small>{dragOperation === 'copy' ? '(Shift gedrückt)' : '(Shift = Kopieren)'}</small>
          </div>
        </div>
      )}

      <div className="browser-header">
        <div className="breadcrumb-nav">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              {getBreadcrumbs().map((crumb, index) => (
                <li
                  key={crumb.path}
                  className={`breadcrumb-item ${index === getBreadcrumbs().length - 1 ? 'active' : ''}`}
                >
                  {index === getBreadcrumbs().length - 1 ? (
                    crumb.name
                  ) : (
                    <button
                      type="button"
                      className="btn btn-link p-0 align-baseline"
                      onClick={(e) => { e.preventDefault(); handleNavigate(crumb.path); }}
                    >
                      {crumb.name}
                    </button>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        </div>

        <Toolbar
          currentPath={currentPath}
          selectedItems={selectedItems}
          clipboard={sharedClipboard}
          onNavigateUp={handleNavigateUp}
          onRefresh={handleRefresh}
          onUpload={() => setShowUploadModal(true)}
          onCreateFolder={() => setShowCreateModal(true)}
          onCopy={handleCopy}
          onCut={handleCut}
          onRename={handleRename}
          onChmod={handleChmod}
          onDelete={handleDelete}
          setClipboard={setSharedClipboard}
          onPaste={handlePaste}
          onZip={handleZip}
          onUnzip={handleUnzip}
          onDiskUsage={handleDiskUsage}
          disabled={isLoading}
          baseApiUrl={baseApiUrl}
        />
      </div>

      {isLoading ? (
        <div className="text-center p-5">
          <div className="spinner-wrap"><div className="css-spinloader"></div></div>
          <span>Lade Dateien...</span>
        </div>
      ) : (
        <FileList
          files={sortedFiles}
          currentPath={currentPath}
          selectedItems={selectedItems}
          selectedItemPaths={selectedItemPaths}
          sortConfig={sortConfig}
          onSort={handleSort}
          onSelectItem={handleSelectItem}
          onItemClick={handleItemClick}
          onDoubleClick={handleDoubleClick}
          onDropOnItem={handleItemDrop}
          onContextMenu={handleContextMenu}
          paneId={paneId}
          renamingItem={renamingItem}
          focusedIndex={lastSelectedIndex}
          isVisible={isVisible}
          onRenameCancel={handleRenameCancel}
          onRenameSubmit={handleRenameSubmit}
        />
      )}

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={getContextMenuItems()}
          onClose={() => setContextMenu(null)}
        />
      )}

      {showUploadModal && (
        <UploadModal
          currentPath={uploadPath || currentPath}
          initialFiles={uploadFiles}
          baseApiUrl={baseApiUrl}
          onClose={() => {
            setShowUploadModal(false);
            setUploadFiles([]);
            setUploadPath(null);
          }}
        />
      )}

      {showCreateModal && (
        <CreateItemModal
          currentPath={currentPath}
          baseApiUrl={baseApiUrl}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {showRenameModal && itemToEdit && (
        <RenameModal
          item={itemToEdit}
          currentPath={currentPath}
          baseApiUrl={baseApiUrl}
          onClose={() => {
            setShowRenameModal(false);
            setItemToEdit(null);
          }}
        />
      )}

      {showChmodModal && itemToEdit && (
        <ChmodModal
          item={itemToEdit}
          currentPath={currentPath}
          baseApiUrl={baseApiUrl}
          onClose={() => {
            setShowChmodModal(false);
            setItemToEdit(null);
          }}
        />
      )}

      {showDiskUsageModal && (
        <DiskUsageModal
          paths={diskUsagePaths}
          currentPath={currentPath}
          baseApiUrl={baseApiUrl}
          onNavigate={handleNavigate}
          onClose={() => {
            setShowDiskUsageModal(false);
            setDiskUsagePaths(null);
          }}
        />
      )}
    </div>
  );
};

FileBrowser.propTypes = {
  paneId: PropTypes.string.isRequired,
  className: PropTypes.string,
  sharedClipboard: PropTypes.object,
  setSharedClipboard: PropTypes.func.isRequired,
  baseApiUrl: PropTypes.string,
  onEditFile: PropTypes.func.isRequired,
  onViewImage: PropTypes.func.isRequired,
  onPathChange: PropTypes.func,
  isVisible: PropTypes.bool,
};

export default FileBrowser;
